//#region IMPORT
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import useCompanyCustDep from '../../../../hooks/useCompanyCustDep';
import GON_BASIC from './GON_BASIC';
import {
  useSearch1,
  useSearch2,
  useSave1,
  useDelete1,
} from './GON_BASIC_QUERY';
import useDelete from '../../../../hooks/useDeleteN';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
//#endregion

const GetNextGongjongNo = (rows) =>
  (rows || []).reduce((max, row) => Math.max(max, Number(row?.no) || 0), 0) + 1;

const SyncGongjongRowsFromGrid = (gridRef, fallbackRows) => {
  const syncedRows = [];
  gridRef?.current?.api?.forEachNode((node) => {
    if (node.data) syncedRows.push({ ...node.data });
  });
  return syncedRows.length > 0 ? syncedRows : fallbackRows || [];
};

/**
 * 공종 관리 기능단 (Gongjong)
 * - 왼쪽: 부서 목록 조회 (GetDepLast → useSearch1)
 * - 오른쪽: 선택 부서의 공정 목록 (GetGonBasicR1 → useSearch2, 부서 클릭 시)
 * - 조회조건: 사업장
 */
function GON_BASICF() {
  const dispatch = useDispatch();
  const gridRef1 = useRef();
  const gridRef2 = useRef();
  const currentRowIndexRef1 = useRef(null);
  const selectedRowNosRef1 = useRef(new Set());
  const selectedRowNosRef2 = useRef(new Set());
  const lastCheckedIndexRef2 = useRef(null);

  const [rowData1, setRowData1] = useState([]);
  const [rowData2, setRowData2] = useState([]);
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [selectedRowData2, setSelectedRowData2] = useState([]);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [changedRowData2, setChangedRowData2] = useState([]);
  const [deletedRowData2, setDeletedRowData2] = useState([]);
  const [readyToExport, setReadyToExport] = useState(false);

  const fileInputRef = useRef(null);

  const { handleCoChange, handleCustChange, validateSelection } =
    useCompanyCustDep({ requireCompany: true, requireCust: true });

  const searchInfo = useSelector((state) => state.SearchInfo);
  const { userInfo } = useSelector((state) => state.BaseInfo);

  //#region 조회
  const { refetch: Search1 } = useSearch1({
    onSuccess: setRowData1,
  });

  const { run: Search2, runAsync: Search2Async } = useSearch2({
    onSuccess: (rows) => {
      const mapped = (rows || []).map((r, i) => ({
        no: r.no ?? i + 1,
        gongjongCd: r.GongjongCd ?? r.gongjongCd ?? '',
        gongjongNm: r.GongjongNm ?? r.gongjongNm ?? '',
        depCd: r.DepCd ?? r.depCd ?? '',
      }));
      setRowData2(mapped);
    },
  });

  const SearchLogic1 = () => {
    if (!validateSelection()) return;
    setRowData2([]);
    setSelectedRowData1([]);
    setSelectedRowData2([]);
    gridRef2?.current?.api?.deselectAll();
    Search1();
  };

  const SearchLogic2 = (dep) => {
    if (!dep) {
      setRowData2([]);
      return;
    }
    Search2({ depCd: dep.depCd });
  };
  //#endregion

  //#region 엑셀 다운로드 (부서명 콤보 + 공정명만, RIS_BASIC_01 방식: 숨김 시트 + 이름 정의)
  useEffect(() => {
    if (!readyToExport || !rowData2?.length) {
      if (readyToExport) setReadyToExport(false);
      return;
    }

    const headers = ['부서명', '공정명'];
    const depList = rowData1 || [];
    const depNameList = [
      ...new Set(
        depList
          .map((d) =>
            (
              d.depNmLast ||
              d.depNm4 ||
              d.depNm3 ||
              d.depNm2 ||
              d.depNm1 ||
              ''
            ).trim()
          )
          .filter(Boolean)
      ),
    ];
    const getDepNmByDepCd = (depCd) => {
      const dep = depList.find((d) => d.depCd === depCd);
      return dep
        ? dep.depNmLast ||
            dep.depNm4 ||
            dep.depNm3 ||
            dep.depNm2 ||
            dep.depNm1 ||
            ''
        : '';
    };

    (async () => {
      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('공정목록', {
          views: [{ state: 'frozen', ySplit: 1 }],
        });

        if (depNameList.length > 0) {
          const sheetMaster = workbook.addWorksheet('Data_Master');
          sheetMaster.state = 'hidden';
          sheetMaster.getCell('A1').value = '부서목록';
          depNameList.forEach((name, i) => {
            sheetMaster.getCell(i + 2, 1).value = name;
          });
          const lastRow = depNameList.length + 1;
          workbook.definedNames.add(
            `Data_Master!$A$2:$A$${lastRow}`,
            '부서목록'
          );
        }

        const headerRow = worksheet.getRow(1);
        headers.forEach((header, i) => {
          const cell = headerRow.getCell(i + 1);
          cell.value = header;
          cell.font = { name: 'Arial', size: 10, bold: true };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' },
          };
          cell.alignment = { vertical: 'middle', wrapText: false };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF808080' } },
            left: { style: 'thin', color: { argb: 'FF808080' } },
            bottom: { style: 'thin', color: { argb: 'FF808080' } },
            right: { style: 'thin', color: { argb: 'FF808080' } },
          };
        });
        headerRow.commit();

        const startRow = 2;
        rowData2.forEach((item, index) => {
          const row = worksheet.getRow(startRow + index);
          const depNm = getDepNmByDepCd(item.depCd);
          const gongjongNm =
            item.gongjongNm != null ? String(item.gongjongNm) : '';
          row.getCell(1).value = depNm;
          row.getCell(2).value = gongjongNm;
          [row.getCell(1), row.getCell(2)].forEach((cell) => {
            cell.font = { name: 'Arial', size: 10 };
            cell.alignment = { vertical: 'middle', wrapText: false };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FF808080' } },
              left: { style: 'thin', color: { argb: 'FF808080' } },
              bottom: { style: 'thin', color: { argb: 'FF808080' } },
              right: { style: 'thin', color: { argb: 'FF808080' } },
            };
          });
          row.commit();
        });

        if (depNameList.length > 0) {
          const lastDataRow = startRow + rowData2.length - 1;
          for (let r = startRow; r <= lastDataRow; r++) {
            worksheet.getCell(r, 1).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: ['부서목록'],
            };
          }
        }

        const depNameMaxLen =
          depNameList.length > 0
            ? Math.max(...depNameList.map((n) => String(n).length))
            : 6;
        worksheet.getColumn(1).width = Math.min(
          Math.max(depNameMaxLen + 2, 10),
          50
        );
        let maxNm = 8;
        rowData2.forEach((item) => {
          maxNm = Math.max(maxNm, String(item.gongjongNm ?? '').length);
        });
        worksheet.getColumn(2).width = Math.min(Math.max(maxNm + 2, 10), 50);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = dayjs().format('YYYYMMDDHHmm') + '_공정목록.xlsx';
        link.click();
        URL.revokeObjectURL(link.href);
        dispatch(
          showSnackbar({
            message: '엑셀 다운로드가 완료되었습니다.',
            type: 'success',
          })
        );
      } catch (err) {
        console.error(err);
        dispatch(
          showSnackbar({
            message: '엑셀 다운로드 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
      } finally {
        setReadyToExport(false);
      }
    })();
  }, [readyToExport, rowData2, rowData1, dispatch]);
  //#endregion

  //#region 저장
  const { runAsync: Save1Async } = useSave1({ search: searchInfo, userInfo });
  //#endregion

  //#region 삭제
  const delete1 = useDelete1();
  const deleteHandler2 = useDelete({
    gridRef: gridRef2,
    deleteMutation: delete1,
    updateDeleted: setDeletedRowData2,
    updateRowData: setRowData2,
    deletedRowData: deletedRowData2,
    rowData: rowData2,
    setChangedRowData: setChangedRowData2,
  });
  //#endregion

  const handleRowSelection1 = (rowData, rowIndex) => {
    currentRowIndexRef1.current = rowIndex;
    setSelectedRowData1([rowData]);
    const gridApi = gridRef1.current?.api;
    if (gridApi) {
      gridApi.getDisplayedRowAtIndex(rowIndex)?.setSelected(true, false);
    }
  };

  const handleRowSelection2 = () => {
    const nodes = gridRef2.current?.api?.getSelectedNodes() ?? [];
    setSelectedRowData2(nodes.map((n) => n.data));
  };

  const AddClick2 = () => {
    const dep = selectedRowData1?.[0];
    if (!dep) {
      dispatch(
        showSnackbar({
          message: '부서를 선택한 후 추가하세요.',
          type: 'warning',
        })
      );
      return;
    }
    setRowData2((prev) => {
      const baseRows = SyncGongjongRowsFromGrid(gridRef2, prev);
      const newNo = GetNextGongjongNo(baseRows);
      return [
        ...baseRows,
        {
          no: newNo,
          clientRowId: uuidv4(),
          gongjongCd: '',
          gongjongNm: '',
          depCd: dep.depCd,
          newYn: 'Y',
        },
      ];
    });
  };

  const MarkedForDeletion2 = () => {
    if (!selectedRowData2?.length) {
      dispatch(
        showSnackbar({
          message: '삭제할 행을 선택하세요.',
          type: 'warning',
        })
      );
      return;
    }
    const newRowExists = selectedRowData2.some((row) => row.newYn === 'Y');
    if (newRowExists) {
      dispatch(
        showSnackbar({
          message: '데이터 입력중입니다. 저장 후 시도하세요.',
          type: 'warning',
        })
      );
      return;
    }
    deleteHandler2.MarkedForDeletion(selectedRowData2);
  };

  const FileUploadClick2 = () => {
    if (!rowData1?.length) {
      dispatch(
        showSnackbar({
          message:
            '사업장 조회 후 부서 목록이 로드된 상태에서 엑셀 업로드하세요.',
          type: 'warning',
        })
      );
      return;
    }
    if (!validateSelection()) return;
    fileInputRef.current?.click();
  };

  const getDepCdByDepNm = (depNm) => {
    const name = String(depNm ?? '').trim();
    if (!name) return null;
    const dep = (rowData1 || []).find(
      (d) =>
        (
          d.depNmLast ||
          d.depNm4 ||
          d.depNm3 ||
          d.depNm2 ||
          d.depNm1 ||
          ''
        ).trim() === name
    );
    return dep ? dep.depCd : null;
  };

  const handleExcelFileChange = (event) => {
    const fileList = event?.target?.files;
    if (!fileList?.length) return;

    if (!rowData1?.length) {
      dispatch(
        showSnackbar({
          message: '부서 목록이 없습니다. 사업장 조회 후 다시 시도하세요.',
          type: 'warning',
        })
      );
      return;
    }

    const file = fileList[0];
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.xls', '.xlsx'].includes(ext)) {
      dispatch(
        showSnackbar({
          message: '엑셀파일만 가능합니다.',
          type: 'warning',
        })
      );
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (e) => {
      try {
        const bstr = e.target?.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const fileData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: '',
        });
        if (!Array.isArray(fileData) || fileData.length < 2) {
          dispatch(
            showSnackbar({
              message: '엑셀에 데이터가 없습니다.',
              type: 'warning',
            })
          );
          return;
        }

        const headerRow = fileData[0].map((h) => String(h ?? '').trim());
        const depNmIdx = headerRow.findIndex((h) => h === '부서명');
        const nmIdx = headerRow.findIndex((h) => h === '공정명');
        const noIdx = headerRow.findIndex((h) => h === 'NO' || h === 'no');
        const cdIdx = headerRow.findIndex((h) => h === '공정코드');
        if (nmIdx === -1) {
          dispatch(
            showSnackbar({
              message: '엑셀 양식이 올바르지 않습니다. (공정명 컬럼 필요)',
              type: 'error',
            })
          );
          return;
        }
        if (depNmIdx === -1) {
          dispatch(
            showSnackbar({
              message: '엑셀 양식이 올바르지 않습니다. (부서명 컬럼 필요)',
              type: 'error',
            })
          );
          return;
        }

        const newRows = [];
        const skipped = [];
        let nextNo = GetNextGongjongNo(rowData2);
        for (let i = 1; i < fileData.length; i++) {
          const row = fileData[i];
          if (!Array.isArray(row)) continue;
          const depNmFromExcel = String(row[depNmIdx] ?? '').trim();
          const gongjongNm = String(row[nmIdx] ?? '').trim();
          if (!gongjongNm) continue;
          const depCd = getDepCdByDepNm(depNmFromExcel);
          if (!depCd) {
            skipped.push(i + 1);
            continue;
          }
          const gongjongCd = cdIdx >= 0 ? String(row[cdIdx] ?? '').trim() : '';
          const noVal =
            noIdx >= 0 && row[noIdx] != null && row[noIdx] !== ''
              ? row[noIdx]
              : null;
          const no =
            noVal != null && !Number.isNaN(Number(noVal))
              ? Number(noVal)
              : nextNo++;
          newRows.push({
            no,
            clientRowId: uuidv4(),
            gongjongNm,
            gongjongCd,
            depCd,
            newYn: 'Y',
          });
        }

        if (newRows.length === 0) {
          dispatch(
            showSnackbar({
              message:
                skipped.length > 0
                  ? `업로드할 수 있는 행이 없습니다. 부서명이 왼쪽 그리드 부서 목록과 일치하는지 확인하세요. (건너뛴 행: ${skipped.join(', ')})`
                  : '업로드할 유효한 행이 없습니다.',
              type: 'warning',
            })
          );
          return;
        }

        if (skipped.length > 0) {
          dispatch(
            showSnackbar({
              message: `${newRows.length}건 업로드되었습니다. 부서명 미일치로 ${skipped.length}건 건너뜀 (행: ${skipped.join(', ')})`,
              type: 'info',
            })
          );
        }
        setRowData2((prev) => [...prev, ...newRows]);
        setChangedRowData2((prev) => [...prev, ...newRows]);
        dispatch(
          showSnackbar({
            message: '엑셀 업로드가 완료되었습니다.',
            type: 'success',
          })
        );
      } catch (err) {
        console.error(err);
        dispatch(
          showSnackbar({
            message: '엑셀 업로드 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
      }
      event.target.value = '';
    };
    reader.onerror = () => {
      dispatch(
        showSnackbar({
          message: '파일 읽기 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    };
  };

  const FileDownloadClick2 = () => {
    if (!rowData2?.length) {
      dispatch(
        showSnackbar({
          message: '다운로드할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    setReadyToExport(true);
  };

  const SaveLogic2 = async () => {
    const dep = selectedRowData1?.[0];
    if (!dep) {
      dispatch(
        showSnackbar({
          message: '부서를 선택한 후 저장하세요.',
          type: 'warning',
        })
      );
      return;
    }
    if (!validateSelection()) return;

    const hasDeleted = deletedRowData2?.length > 0;

    const currentRows = [];
    gridRef2.current?.api?.forEachNode(
      (node) => node.data && currentRows.push(node.data)
    );
    const sourceRows = currentRows.length > 0 ? currentRows : rowData2;

    const newRows = sourceRows.filter(
      (r) => r.newYn === 'Y' && (r.gongjongNm || '').trim()
    );
    const editedRows = (changedRowData2 || []).filter((r) => !r.newYn);
    const rowsToSave = [...newRows, ...editedRows];

    if (rowsToSave.length === 0 && !hasDeleted) {
      dispatch(
        showSnackbar({
          message:
            '저장할 데이터가 없습니다. 공정명을 입력한 신규 행을 추가하거나 수정 후 저장하세요.',
          type: 'warning',
        })
      );
      return;
    }

    try {
      if (hasDeleted) {
        const deleteResult = await deleteHandler2.DeleteLogic();
        if (deleteResult && !deleteResult.isSuccess) {
          dispatch(
            showSnackbar({
              message: deleteResult?.Message || '삭제 중 오류가 발생했습니다.',
              type: 'error',
            })
          );
          return;
        }
      }

      if (rowsToSave.length > 0) {
        const response = await Save1Async(rowsToSave);
        const results = Array.isArray(response)
          ? response
          : (response?.data ?? []);
        const allSuccess =
          results.length > 0 && results.every((r) => r?.IsSuccess !== false);
        if (!allSuccess) {
          const errMsg =
            results.find((r) => !r?.IsSuccess)?.Message ||
            '저장 중 오류가 발생했습니다.';
          dispatch(
            showSnackbar({
              message: errMsg,
              type: 'error',
            })
          );
          return;
        }
      }

      dispatch(
        showSnackbar({
          message: '저장되었습니다.',
          type: 'success',
        })
      );
      setChangedRowData2([]);
      setSelectedRowData2([]);
      gridRef2.current?.api?.deselectAll();
      await Search2Async({ depCd: dep.depCd });
    } catch {
      dispatch(
        showSnackbar({
          message: '저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  return (
    <div className="wrap">
      <GON_BASIC
        gridRef1={gridRef1}
        gridRef2={gridRef2}
        rowData1={rowData1}
        rowData2={rowData2}
        setRowData1={setRowData1}
        setRowData2={setRowData2}
        selectedRowData1={selectedRowData1}
        setSelectedRowData1={setSelectedRowData1}
        selectedRowData2={selectedRowData2}
        setSelectedRowData2={setSelectedRowData2}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        changedRowData2={changedRowData2}
        setChangedRowData2={setChangedRowData2}
        selectedRowNosRef1={selectedRowNosRef1}
        selectedRowNosRef2={selectedRowNosRef2}
        lastCheckedIndexRef2={lastCheckedIndexRef2}
        currentRowIndexRef1={currentRowIndexRef1}
        DeleteNewRow2={deleteHandler2.DeleteNewRow}
        RestoreRow2={deleteHandler2.RestoreRow}
        handleRowSelection2={handleRowSelection2}
        validateSelection={validateSelection}
        userInfo={userInfo}
        handleCoChange={handleCoChange}
        handleCustChange={handleCustChange}
        SearchLogic1={SearchLogic1}
        SearchLogic2={SearchLogic2}
        handleRowSelection1={handleRowSelection1}
        AddClick2={AddClick2}
        MarkedForDeletion2={MarkedForDeletion2}
        FileUploadClick2={FileUploadClick2}
        FileDownloadClick2={FileDownloadClick2}
        fileInputRef={fileInputRef}
        onExcelFileChange={handleExcelFileChange}
        SaveLogic2={SaveLogic2}
      />
    </div>
  );
}

export default GON_BASICF;
